import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import QuickActionFAB from '../../components/ui/QuickActionFAB';
import ReportCategories from './components/ReportCategories';
import VisualizationArea from './components/VisualizationArea';
import FilterControls from './components/FilterControls';
import DashboardOverview from './components/DashboardOverview';
import Icon from '../../components/AppIcon';

const ReportsAndAnalytics = () => {
  const [selectedCategory, setSelectedCategory] = useState('dashboard');
  const [selectedReport, setSelectedReport] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: '2025-01-01',
    endDate: '2025-07-12'
  });
  const [filters, setFilters] = useState({
    department: '',
    product: '',
    customer: '',
    region: ''
  });
  const [isLoading, setIsLoading] = useState(true);
  const [reportData, setReportData] = useState({});

  // Report categories
  const categories = [
    {
      id: 'dashboard',
      name: 'Panel Principal',
      icon: 'LayoutDashboard',
      description: 'Resumen ejecutivo con métricas clave'
    },
    {
      id: 'sales',
      name: 'Ventas',
      icon: 'TrendingUp',
      description: 'Análisis de rendimiento de ventas'
    },
    {
      id: 'inventory',
      name: 'Inventario',
      icon: 'Package',
      description: 'Rotación y análisis de stock'
    },
    {
      id: 'customers',
      name: 'Clientes',
      icon: 'Users',
      description: 'Comportamiento y segmentación'
    },
    {
      id: 'financial',
      name: 'Financiero',
      icon: 'Euro',
      description: 'Rentabilidad y análisis financiero'
    },
    {
      id: 'operations',
      name: 'Operaciones',
      icon: 'Settings',
      description: 'Eficiencia operacional'
    }
  ];

  // Available reports by category
  const reports = {
    dashboard: [
      { id: 'executive', name: 'Resumen Ejecutivo', type: 'dashboard' },
      { id: 'kpi', name: 'KPIs Principales', type: 'metrics' }
    ],
    sales: [
      { id: 'monthly-sales', name: 'Ventas Mensuales', type: 'chart' },
      { id: 'sales-by-product', name: 'Ventas por Producto', type: 'chart' },
      { id: 'sales-forecast', name: 'Previsión de Ventas', type: 'chart' },
      { id: 'sales-performance', name: 'Rendimiento del Equipo', type: 'table' }
    ],
    inventory: [
      { id: 'stock-levels', name: 'Niveles de Stock', type: 'chart' },
      { id: 'inventory-turnover', name: 'Rotación de Inventario', type: 'chart' },
      { id: 'aging-report', name: 'Análisis de Antigüedad', type: 'table' },
      { id: 'low-stock', name: 'Productos con Stock Bajo', type: 'table' }
    ],
    customers: [
      { id: 'customer-segmentation', name: 'Segmentación de Clientes', type: 'chart' },
      { id: 'customer-lifetime', name: 'Valor del Cliente', type: 'chart' },
      { id: 'purchase-patterns', name: 'Patrones de Compra', type: 'chart' },
      { id: 'customer-retention', name: 'Retención de Clientes', type: 'metrics' }
    ],
    financial: [
      { id: 'profitability', name: 'Análisis de Rentabilidad', type: 'chart' },
      { id: 'cash-flow', name: 'Flujo de Caja', type: 'chart' },
      { id: 'expense-analysis', name: 'Análisis de Gastos', type: 'chart' },
      { id: 'roi-analysis', name: 'Análisis ROI', type: 'table' }
    ],
    operations: [
      { id: 'delivery-performance', name: 'Rendimiento de Entregas', type: 'chart' },
      { id: 'order-fulfillment', name: 'Cumplimiento de Pedidos', type: 'metrics' },
      { id: 'efficiency-metrics', name: 'Métricas de Eficiencia', type: 'table' },
      { id: 'process-optimization', name: 'Optimización de Procesos', type: 'chart' }
    ]
  };

  useEffect(() => {
    // Simulate data loading
    const timer = setTimeout(() => {
      setIsLoading(false);
      // Set default report for dashboard
      if (selectedCategory === 'dashboard') {
        setSelectedReport(reports.dashboard[0]);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [selectedCategory]);

  const handleCategorySelect = (category) => {
    setSelectedCategory(category.id);
    setSelectedReport(null);
    setIsLoading(true);
  };

  const handleReportSelect = (report) => {
    setSelectedReport(report);
  };

  const handleDateRangeChange = (newDateRange) => {
    setDateRange(newDateRange);
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleExportReport = (format) => {
    // Simulate export functionality
    console.log(`Exporting report in ${format} format`);
  };

  const handleScheduleReport = (schedule) => {
    // Simulate report scheduling
    console.log('Scheduling report:', schedule);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-center h-64">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <span className="text-lg text-text-secondary">Cargando análisis y reportes...</span>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <BreadcrumbNavigation />
          
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-2xl lg:text-3xl font-heading-semibold text-text-primary mb-2">
                  Reportes y Análisis
                </h1>
                <p className="text-text-secondary font-body-normal">
                  Inteligencia empresarial integral a través de paneles interactivos y herramientas de análisis
                </p>
              </div>
              
              <div className="mt-4 sm:mt-0 flex items-center space-x-3">
                <button 
                  onClick={() => handleExportReport('pdf')}
                  className="inline-flex items-center px-4 py-2 border border-border text-sm font-body-medium rounded-md text-text-secondary bg-surface hover:bg-secondary-50 transition-micro"
                >
                  <Icon name="FileText" size={16} className="mr-2" />
                  Exportar PDF
                </button>
                <button 
                  onClick={() => handleExportReport('excel')}
                  className="inline-flex items-center px-4 py-2 border border-border text-sm font-body-medium rounded-md text-text-secondary bg-surface hover:bg-secondary-50 transition-micro"
                >
                  <Icon name="Download" size={16} className="mr-2" />
                  Exportar Excel
                </button>
                <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-body-medium rounded-md text-white bg-primary hover:bg-primary-600 transition-micro">
                  <Icon name="Plus" size={16} className="mr-2" />
                  Nuevo Reporte
                </button>
              </div>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Report Categories Sidebar - 3 columns */}
            <div className="lg:col-span-3">
              <ReportCategories 
                categories={categories}
                selectedCategory={selectedCategory}
                onCategorySelect={handleCategorySelect}
                reports={reports}
                selectedReport={selectedReport}
                onReportSelect={handleReportSelect}
              />
            </div>

            {/* Visualization Area - 6 columns */}
            <div className="lg:col-span-6">
              {selectedCategory === 'dashboard' && !selectedReport ? (
                <DashboardOverview />
              ) : (
                <VisualizationArea 
                  selectedReport={selectedReport}
                  selectedCategory={selectedCategory}
                  dateRange={dateRange}
                  filters={filters}
                  onExport={handleExportReport}
                />
              )}
            </div>

            {/* Filter Controls Panel - 3 columns */}
            <div className="lg:col-span-3">
              <FilterControls 
                dateRange={dateRange}
                filters={filters}
                onDateRangeChange={handleDateRangeChange}
                onFiltersChange={handleFiltersChange}
                onScheduleReport={handleScheduleReport}
                selectedCategory={selectedCategory}
              />
            </div>
          </div>

          {/* Quick Stats for Mobile */}
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4 lg:hidden">
            <div className="card p-4 text-center">
              <div className="text-xl font-heading-semibold text-primary mb-1">
                12
              </div>
              <div className="text-xs text-text-secondary">Reportes Activos</div>
            </div>
            <div className="card p-4 text-center">
              <div className="text-xl font-heading-semibold text-success mb-1">
                5
              </div>
              <div className="text-xs text-text-secondary">Programados</div>
            </div>
            <div className="card p-4 text-center">
              <div className="text-xl font-heading-semibold text-warning mb-1">
                3
              </div>
              <div className="text-xs text-text-secondary">Pendientes</div>
            </div>
            <div className="card p-4 text-center">
              <div className="text-xl font-heading-semibold text-info mb-1">
                24h
              </div>
              <div className="text-xs text-text-secondary">Última Sincronización</div>
            </div>
          </div>
        </div>
      </main>

      <QuickActionFAB />
    </div>
  );
};

export default ReportsAndAnalytics;